import { yupResolver } from '@hookform/resolvers/yup';
import { AvailableCurrencyDropdown } from '@src/clients/financialSettings/AvailableCurrencyDropdown';
import { BaseModal } from '@src/common/components/BaseModal';
import { Button } from '@src/common/components/button/Button';
import { Divider } from '@src/common/components/Divider';
import { FormTextInput } from '@src/common/components/FormTextInput';
import { MoneyInput } from '@src/common/components/MoneyInput';
import { Stack } from '@src/common/components/Stack';
import { theme } from '@src/common/styles/Theme';
import type { AdditionalFee } from '@src/orders/domain/ReviewableOrder';
import { FormCheckbox } from '@src/orders/PermitFormSections/FormCheckbox';
import {
  AddFeeSchema,
  AmountField,
  DescriptionField,
  TaxableField,
} from '@src/orders/schemas/ReviewOrderFormPermitFields';
import { type SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { v4 as uuid } from 'uuid';
import type * as yup from 'yup';

interface Props {
  open: boolean;
  onAdd: (data: AdditionalFee) => void;
  onClose: () => void;
}

export const AddFeeForm = ({ open, onAdd, onClose }: Props) => {
  const { t } = useTranslation();

  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    resolver: yupResolver(AddFeeSchema),
  });

  const addFee: SubmitHandler<yup.InferType<typeof AddFeeSchema>> = (data) => {
    onAdd({
      id: uuid().toString(),
      amount: data.amount,
      description: data.description,
      currency: data.currency,
      taxable: data.taxable,
    });
    reset(undefined);
  };

  return (
    <BaseModal
      title={t('reviewPermit.otherFees')}
      subtitle={t('reviewPermit.addFeeSubtitle')}
      open={open}
      onClose={onClose}
    >
      <form
        onSubmit={(e) => {
          e.stopPropagation();
          handleSubmit(addFee)(e);
        }}
      >
        <Stack gap={theme.spacing['8']}>
          <Divider />

          <Stack direction="row" gap={theme.spacing['6']}>
            <MoneyInput
              size="medium"
              register={register}
              fieldName={AmountField.fieldName}
              label={t('amount')}
              errors={errors}
              noLabel={false}
            />
            <AvailableCurrencyDropdown control={control} />
            <FormCheckbox
              register={register}
              fieldName={TaxableField.fieldName}
            />
          </Stack>

          <FormTextInput
            register={register}
            fieldName={DescriptionField.fieldName}
            multiline
            errors={errors}
          />

          <Divider />

          <Stack direction="row" justifyContent="end" gap={theme.spacing['2']}>
            <Button type="button" onClick={onClose} variant="outlined">
              {t('cancel')}
            </Button>

            <Button type="submit" variant="contained">
              {t('addFee')}
            </Button>
          </Stack>
        </Stack>
      </form>
    </BaseModal>
  );
};
